import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, List, Eye, Edit, Loader } from 'lucide-react';



import SurveyBuilder from './SurveyBuilder';
import SurveyList from './SurveyList';
import SurveyView from './SurveyView';


import surveyService from '../../services/surveyService';
import studyService from '../../services/studyService';

const Survey = () => {
  const [activeTab, setActiveTab] = useState('list');
  const [surveys, setSurveys] = useState([]);
  const [studies, setStudies] = useState([]);
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchSurveys();
    fetchStudies();
  }, []);

  const fetchSurveys = async () => {
    try {
      setIsLoading(true);
      const response = await surveyService.getAllSurveys();
      setSurveys(response);
      setError(null);
    } catch (error) {
      console.error('Error fetching surveys:', error);
      setError('Unable to fetch surveys. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStudies = async () => {
    try {
      const response = await studyService.getStudies();
      setStudies(response);
    } catch (error) {
      console.error('Error fetching studies:', error);
      setError('Unable to fetch studies. Please try again later.');
    }
  };

  const handleCreateSurvey = async (surveyData) => {
    try {
      setIsLoading(true);
      await surveyService.createSurvey(surveyData);
      await fetchSurveys();
      setActiveTab('list');
      setError(null);
    } catch (error) {
      console.error('Error creating survey:', error);
      // Re-throw the error to let SurveyBuilder handle the detailed error message
      throw error;
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleEditSurvey = async (surveyData) => {
    try {
      setIsLoading(true);
      await surveyService.updateSurvey(surveyData.id, surveyData);
      await fetchSurveys();
      setActiveTab('list');
      setSelectedSurvey(null);
      setError(null);
    } catch (error) {
      console.error('Error updating survey:', error);
      // Re-throw the error to let SurveyBuilder handle the detailed error message
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenEditSurvey = (survey) => {
    setSelectedSurvey(survey);
    setActiveTab('edit');
  };

  const handleViewSurvey = (survey) => {
    setSelectedSurvey(survey);
    setActiveTab('view');
  };

  const handleCancelEdit = () => {
    setSelectedSurvey(null);
    setActiveTab('list');
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex justify-center items-center h-64">
          <Loader className="w-8 h-8 text-primary-600 animate-spin" />
        </div>
      );
    }

    if (error) {
      return (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded-md">
          <p className="font-bold">Error</p>
          <p>{error}</p>
        </div>
      );
    }

    switch (activeTab) {
      case 'build':
        return <SurveyBuilder onCreateSurvey={handleCreateSurvey} studies={studies} />;
      case 'list':
        return <SurveyList surveys={surveys} onEdit={handleOpenEditSurvey} onView={handleViewSurvey} />;
      case 'view':
        return <SurveyView survey={selectedSurvey} />;
      case 'edit':
        return (
          <SurveyBuilder 
            survey={selectedSurvey} 
            onUpdateSurvey={handleEditSurvey} 
            onCancel={handleCancelEdit}
            studies={studies} 
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-7xl mx-auto bg-white dark:bg-gray-800 dark:shadow-gray-900/20 rounded-2xl shadow-xl overflow-hidden"
      >
        <div className="flex items-center justify-between p-6 bg-gradient-to-r from-primary-500 to-primary-600">
          <h1 className="text-3xl font-bold text-white">Survey Management</h1>
          <div className="flex space-x-4">
            <button
              onClick={() => setActiveTab('build')}
              className={`flex items-center px-4 py-2 rounded-full ${
                activeTab === 'build' ? 'bg-white text-primary-600' : 'bg-primary-700 text-white'
              } transition-colors duration-200`}
            >
              <Plus className="w-5 h-5 mr-2" />
              Create Survey
            </button>
            <button
              onClick={() => setActiveTab('list')}
              className={`flex items-center px-4 py-2 rounded-full ${
                activeTab === 'list' ? 'bg-white dark:bg-gray-800 text-primary-600' : 'bg-primary-700 text-white'
              } transition-colors duration-200`}
            >
              <List className="w-5 h-5 mr-2" />
              View Surveys
            </button>
          </div>
        </div>
        <div className="p-6">
          {renderContent()}
        </div>
      </motion.div>
    </div>
  );
};

export default Survey;